/** Fallback when `Intl.supportedValuesOf('currency')` is unavailable (older runtimes). */
const FALLBACK_ISO_4217 = [
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
  'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD',
  'CAD', 'CDF', 'CHF', 'CLP', 'CNY', 'COP', 'CRC', 'CUP', 'CVE', 'CZK',
  'DJF', 'DKK', 'DOP', 'DZD',
  'EGP', 'ERN', 'ETB', 'EUR',
  'FJD', 'FKP',
  'GBP', 'GEL', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD',
  'HKD', 'HNL', 'HRK', 'HTG', 'HUF',
  'IDR', 'ILS', 'INR', 'IQD', 'IRR', 'ISK',
  'JMD', 'JOD', 'JPY',
  'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
  'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD',
  'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN',
  'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD',
  'OMR',
  'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG',
  'QAR',
  'RON', 'RSD', 'RUB', 'RWF',
  'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SLL', 'SOS', 'SRD', 'SSP', 'STN', 'SVC', 'SYP', 'SZL',
  'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'TZS',
  'UAH', 'UGX', 'USD', 'UYU', 'UZS',
  'VES', 'VND', 'VUV',
  'WST',
  'XAF', 'XCD', 'XOF', 'XPF',
  'YER',
  'ZAR', 'ZMW', 'ZWL'
];

/** Product-friendly names (Intl often returns singular "krone"; we prefer common English plurals where it reads better). */
const CURRENCY_NAME_OVERRIDES: Record<string, string> = {
  NOK: 'Norwegian Kroner',
  SEK: 'Swedish Kronor',
  DKK: 'Danish Kroner',
  ISK: 'Icelandic Krónur'
};

/**
 * Sorted list of ISO 4217 currency codes for pickers (searchable dropdowns).
 * Prefers `Intl.supportedValuesOf` when the runtime supports it.
 */
export function getIso4217CurrencyCodes(): string[] {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    if (typeof intl.supportedValuesOf === 'function') {
      const values = intl.supportedValuesOf('currency');
      if (Array.isArray(values) && values.length > 0) {
        return [...values].map((c) => c.toUpperCase()).sort();
      }
    }
  } catch {
    /* use fallback */
  }
  return [...FALLBACK_ISO_4217];
}

export function filterCurrencyOptions(codes: string[], exclude: Set<string>): string[] {
  return codes.filter((c) => !exclude.has(c.toUpperCase()));
}

/** English display name for a currency code (e.g. NOK → Norwegian Kroner). */
export function getCurrencyNameEn(code: string): string {
  const c = code.trim().toUpperCase();
  if (c.length !== 3) return code;
  const o = CURRENCY_NAME_OVERRIDES[c];
  if (o) return o;
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'currency' });
    const name = dn.of(c);
    if (name) return name;
  } catch {
    /* ignore */
  }
  return c;
}

/** Picker / table label: `NOK — Norwegian Kroner`. */
export function formatCurrencyPickerLabel(code: string): string {
  const c = code.trim().toUpperCase();
  if (c.length !== 3) return code;
  return `${c} — ${getCurrencyNameEn(c)}`;
}
